import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ModulesService} from '../../services/modules.service';
import {BehaviorSubject, forkJoin, interval, Observable, of, Subject} from 'rxjs';
import {IModule} from '../../models/module/IModule';
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {GlobalSearchService, GlobalSearchResult, GroupedSearchResults} from '../../services/global-search.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UserService} from '../../services/user.service';
import {PermissionsService} from '../../services/permissions.service';
import {UserVM} from '../../models/user/user-vm.model';
import {CorporateFormsService} from '../../corporate/services/corporate-forms.service';
import {UserCompany} from '../../models/user/user-companies-response-dto.model';
import {CompaniesService} from '../../services/companies.service';
import {Modules} from '../../enums/modules.enum';
import {NavigationEnd, Router} from '@angular/router';
import {FormTypeEnum} from '../../corporate/enums/form-type.enum';
import { ConfirmDialogComponent } from 'src/app/corporate/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import {RedirectHandlerService} from '../../auth/services/test/redirect-handler.service';
import {MeetingNotesService} from '../../corporate/meeting-notes/services/meeting-notes.service';
import {MeetingNotesPanelService} from '../../corporate/meeting-notes/services/meeting-notes-panel.service';
import {PhonebookSidebarService} from '../../corporate/phonebook';
import {AIChatPanelService} from '../../ai-chat/ai-chat-panel.service';
import { NotificationVM, enrichNotification, isPendingChangeNotification } from '../../notifications/models/notification.model';
import {NgxPermissionsService} from 'ngx-permissions';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (this.isSettingsOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isSettingsOpen = false;
    }
    if (this.isModuleMenuOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isModuleMenuOpen = false;
    }
    if (this.isMobileMenuOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isMobileMenuOpen = false;
    }
  }

  @ViewChild('companyFilter') companyFilter: ElementRef<HTMLInputElement>;

  @Output()
  changeModule = new EventEmitter<number>();

  @Output()
  changeMenu = new EventEmitter();

  @Output()
  managedCompanyChange = new EventEmitter<UserCompany>();

  @Output()
  logout = new EventEmitter<void>();

  @Input()
  loginDisplay: boolean;

  @Input()
  menu: any[] = [];

  public currentUser: UserVM;
  public selectedModuleName$!: BehaviorSubject<string>;
  public activeNavItem: string = '';
  public navDropdownOpen = false;
  public navDropdownItem: any = null;

  private modulesService = inject(ModulesService);
  private userService = inject(UserService);
  private corporatePermissionsService = inject(PermissionsService);
  private formsService = inject(CorporateFormsService);
  private companiesService = inject(CompaniesService);
  private router = inject(Router);
  private readonly meetingNoteService = inject(MeetingNotesService);
  public readonly meetingNotesPanelService = inject(MeetingNotesPanelService);
  public readonly phonebookSidebarService = inject(PhonebookSidebarService);
  public readonly aiChatPanelService = inject(AIChatPanelService);
  private readonly eRef = inject(ElementRef);
  private readonly destroy = inject(DestroyRef);
  private readonly globalSearchService = inject(GlobalSearchService);
  private readonly permissionsService = inject(NgxPermissionsService);

  // Polling for notifications
  private readonly POLLING_INTERVAL = 10000; // 10 seconds
  private isPolling = false;

  public isModuleMenuOpen = false
  public isSettingsOpen = false;
  public isMobileMenuOpen = false;

  // Maps invoicing menu routes to dashboard view/tab when user is on the dashboard
  private readonly dashboardRouteMap: { [route: string]: { view: string; tab: number } } = {
    'invoicing/invoices-new': { view: 'documents', tab: 1 },
    'invoicing/credit-notes': { view: 'documents', tab: 2 },
    'invoicing/quotations': { view: 'documents', tab: 3 },
    'invoicing/receipts': { view: 'documents', tab: 4 },
    'invoicing/sales': { view: 'documents', tab: 5 },
    'invoicing/vendors': { view: 'vendors', tab: 0 },
    'invoicing/expenses': { view: 'vendors', tab: 1 },
    'invoicing/disbursements-management': { view: 'documents', tab: 7 },
    'invoicing/actual-expenses-management': { view: 'documents', tab: 8 },
    'invoicing/new-payments': { view: 'settings', tab: 0 },
    'invoicing/bank-accounts': { view: 'settings', tab: 1 },
    'invoicing/vat-rates': { view: 'settings', tab: 2 },
    'invoicing/number-sequences': { view: 'settings', tab: 3 },
    'invoicing/chart-of-accounts': { view: 'settings', tab: 4 },
  };

  public settings = [
    {
      icon: 'settings',
      name: 'User settings',
      link: '/settings/user'
    },
    {
      icon: 'corporate_fare',
      name: 'Organization settings',
      link: '/settings/organization'
    }
  ];

  public permissions = [
    ['CorporateUser'],
    ['InvoicingUser'],
    ['ComplianceUser']
  ];

  public modules$: Observable<IModule[]>;
  public userCompanies: UserCompany[];
  public managedCompanyId: number;
  public filteredCompanies: UserCompany[];
  public isOnboarding = false;
  isAdmin: boolean = false;

  notifications: NotificationVM[] = []
  get unreadCount(): number {

    //console.log('Unread notifications count:', this.notifications.filter(n => !n.read).length);
    return this.notifications.filter(n => !n.isRead).length;
  }

  // Interval handle for the localStorage-restore lock used by openCompanySettings.
  // Tracked so other actions on this tab (e.g. switchToCompany) can stop the
  // lock before writing their own legitimate managedCompanyId value.
  private settingsLockInterval: any = null;

  // Global Search
  searchControl = new FormControl('');
  searchResults: GroupedSearchResults[] = [];
  isSearching = false;
  showSearchResults = false;
  showSearchFilters = false;
  activeSearchFilters: string[] = [];
  // Empty array = search across all companies the user has access to.
  selectedSearchCompanyIds: number[] = [];

  // Search filter options
  searchFilterOptions = [
    { label: 'Clients', value: 'clients', icon: 'people' },
    { label: 'Invoicing', value: 'invoicing', icon: 'receipt_long' },
    { label: 'Documents', value: 'documents', icon: 'description' },
    { label: 'Planner', value: 'tasks', icon: 'task_alt' },
    { label: 'Ref.No', value: 'refno', icon: 'tag' }
  ];

  viewPendingLeaves(note: any) {
    // Absolute navigation using currentUser.userId as the employee ID
    this.router.navigate(['/employee', this.currentUser.userId, 'pending-leaves']);
  }

  markAsRead(note: NotificationVM, index: number): void {
    this.formsService.updateNotificationBasedOnID(note.id).subscribe(() => {
      const actual = this.notifications.find(n => n.id === note.id);
      if (actual) actual.isRead = true;
    });

    if (note.meetingNoteId) {
      this.redirectToMeetingNote(note);
    } else if (isPendingChangeNotification(note)) {
      this.openPendingChangeNotification(note);
    } else if (note.globalRequestId) {
      const isRequesterUpdate = (note.message || '').toLowerCase().startsWith('your');
      this.router.navigate([isRequesterUpdate ? '/global-requests/my-requests' : '/global-requests/inbox']);
    }
  }

  /**
   * Route a pending-change notification to the admin queue. When the notification belongs to a
   * different company than the one currently managed on this tab, prompt to switch first — the
   * pending-changes page is scoped by managedCompanyId, so opening it under the wrong company
   * would show an empty queue.
   */
  private openPendingChangeNotification(note: NotificationVM): void {
    const target = '/admin/pending-changes';
    const currentCompanyId = this.companiesService.managedCompanyId;
    const targetCompanyId = note.companyId;

    if (targetCompanyId && targetCompanyId !== currentCompanyId) {
      this.confirmCompanySwitch(currentCompanyId, targetCompanyId).subscribe(confirmed => {
        if (!confirmed) return;
        this.switchCompanyAndNavigate(targetCompanyId, target);
      });
      return;
    }

    this.router.navigate([target]);
  }
  markAllAsRead(): void {
    this.formsService.readAllNotifications().subscribe(user => {
      this.notifications.forEach(n => n.isRead = true);
    })
  }


  public isLoading = false; // Added isLoading property

private snackBar = inject(MatSnackBar); // Properly injected MatSnackBar
private dialog = inject(MatDialog);
private msalService = inject(MsalService);
private readonly redirectHandlerService = inject(RedirectHandlerService);
  deleteNotification(note: NotificationVM, index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Confirm Deletion',text: 'Are you sure you want to delete this notification?',subText: 'Yes'}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.formsService.deleteNotificationBasedOnID(note.id).subscribe({
          next: () => {
            this.formsService.getAllNotifications().subscribe({
              next: (notifications) => {
                this.notifications = notifications.map(n => enrichNotification(n));
                this.isLoading = false;
                this.snackBar.open('Notification deleted!', 'Close', {
                  duration: 3000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              },
              error: (error) => {
                this.isLoading = false;
                this.snackBar.open(`Error refreshing notifications: ${error.message}`, 'Close', {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  approveNotification(note: NotificationVM, index: number, status: number): void {
    let statusText = '';
    switch (status) {
      case 0: statusText = 'N/a'; break;
      case 1: statusText = 'Pending'; break;
      case 2: statusText = 'Approved'; break;
      case 3: statusText = 'Rejected'; break;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Action',
        text: `Are you sure you want to mark this notification as "${statusText}"?`,
        subText: 'Confirm'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.formsService.leaveUpdateBasedOnID(note.timeSheetEntryId, status).subscribe({
          next: () => {
            this.formsService.getAllNotifications().subscribe({
              next: (notifications) => {
                this.notifications = notifications.map(n => enrichNotification(n));
                this.isLoading = false;
                this.snackBar.open(`Notification marked as "${statusText}"!`, 'Close', {
                  duration: 3000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              },
              error: (error) => {
                this.isLoading = false;
                this.snackBar.open(`Error refreshing notifications: ${error.message}`, 'Close', {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          }
        });
      }
    });
  }

  ngOnInit() {
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      this.isOnboarding = event.url.includes('auth/form');
    });

    this.modules$ = this.modulesService.getModules();
    const urlParams = new URLSearchParams(window.location.search);
    const moduleFromUrl = +(urlParams.get('module') || 0);
    const selectedModule = moduleFromUrl || this.modulesService.getSelectedModule() || 1;

    const applyModule = (moduleId: number) => {
      this.modulesService.selectModule(moduleId);
      this.changeModule.emit(moduleId);
      this.changeMenuModule(moduleId);
    };

    this.modulesService.hasModuleAccess(selectedModule).then(hasAccess => {
      if (hasAccess) {
        applyModule(selectedModule);
      } else {
        this.modulesService.getFirstAccessibleModule().then(fallbackModule => {
          const safeModule = fallbackModule || 1;
          applyModule(safeModule);
          this.router.navigate([], {
            queryParams: { module: safeModule },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        });
      }
    });

    this.redirectHandlerService.waitForRedirect().then(() => {
      // Check if logged in before fetching
      if (this.msalService.instance.getAllAccounts().length > 0) {
        // Ensure module is in the URL so each tab is self-contained (only on home page, after auth)
        if (!moduleFromUrl && window.location.pathname === '/') {
          this.router.navigate([], {
            queryParams: { module: selectedModule },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
        this.userService.getCurrentUser(false).pipe(
          catchError((err) => {
            console.error('Error fetching current user:', err);  // Log instead of throw
            return of(null);  // Return fallback observable to continue
          })
        ).subscribe((user) => {
          if (user) {  // Only set if user exists
            this.currentUser = user;
            if (this.corporatePermissionsService.isCorporateAdmin()) {
              this.isAdmin = true;
            }
          }
        });

        this.companiesService.getManagedCompanyIdObsValue().pipe(
          tap((id) => this.managedCompanyId = id)
        ).subscribe();

        this.userService.getUserCompanies().pipe(
          map(userCompanies => userCompanies.companies),
          catchError(err => {
            console.error('Error fetching user companies:', err);  // Log instead of throw
            return of([]);  // Fallback to empty array
          })
        ).subscribe((companies) => {
          const managedCompany = companies.find(company => company.id === this.managedCompanyId);
          this.managedCompanyChange.emit(managedCompany);
          // Sort companies alphabetically by name (ascending)
          const sortedCompanies = [...companies].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
          this.userCompanies = sortedCompanies;
          this.filteredCompanies = sortedCompanies;
        });

        this.formsService.getAllNotifications().subscribe(notifications => {
          this.notifications = notifications.map(n => enrichNotification(n));
          this.startNotificationPolling();
        });

        // Start notes badge polling
        this.meetingNotesPanelService.startBadgePolling();
      } else {
        console.log('No active account - skipping user/company fetches.');
      }
    });

    this.selectedModuleName$ = this.modulesService.moduleName$;

    // Initialize global search
    this.initializeSearch();
  }

  private initializeSearch(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(value => {
        this.isSearching = value && value.length >= 2;
        if (!value || value.length < 2) {
          this.searchResults = [];
          this.showSearchResults = false;
        }
      }),
      filter(value => !!value && value.length >= 2),
      switchMap(value => {
        return this.globalSearchService.search(value!, this.activeSearchFilters, this.selectedSearchCompanyIds).pipe(
          catchError(() => of([]))
        );
      }),
      takeUntilDestroyed(this.destroy)
    ).subscribe(results => {
      this.searchResults = this.filterResultsByCategory(results);
      this.isSearching = false;
      this.showSearchResults = this.searchResults.length > 0;
    });
  }

  private filterResultsByCategory(results: GroupedSearchResults[]): GroupedSearchResults[] {
    if (this.activeSearchFilters.length === 0) {
      return results;
    }

    // Map filter values to categories
    const categoryMap: { [key: string]: string[] } = {
      'clients': ['Clients'],
      'invoicing': ['Invoicing'],
      'documents': ['Documents'],
      'tasks': ['Tasks'],
      'refno': ['Reference Numbers'] // Ref.No shows only Reference Numbers category
    };

    const allowedCategories = new Set<string>();
    this.activeSearchFilters.forEach(filter => {
      const categories = categoryMap[filter] || [];
      categories.forEach(cat => allowedCategories.add(cat));
    });

    return results.filter(group => allowedCategories.has(group.category));
  }

  onSearchResultClick(result: GlobalSearchResult): void {
    this.showSearchResults = false;
    this.showSearchFilters = false;
    this.searchControl.setValue('');

    const currentCompanyId = this.companiesService.managedCompanyId;
    const targetCompanyId = result.companyId;

    if (targetCompanyId && targetCompanyId !== currentCompanyId) {
      this.confirmCompanySwitch(currentCompanyId, targetCompanyId).subscribe(confirmed => {
        if (!confirmed) return;
        if (result.clientData) {
          localStorage.setItem('invoicingClient', JSON.stringify(result.clientData));
        }
        this.switchCompanyAndNavigate(targetCompanyId, result.route, result.queryParams);
      });
      return;
    }

    if (result.clientData) {
      localStorage.setItem('invoicingClient', JSON.stringify(result.clientData));
    }
    if (result.queryParams) {
      this.router.navigate([result.route], { queryParams: result.queryParams });
    } else {
      this.router.navigate([result.route]);
    }
  }

  private switchCompanyAndNavigate(
    companyId: number,
    route: string,
    queryParams?: Record<string, any>
  ): void {
    this.companiesService.setManagedCompanyId(companyId);
    forkJoin([
      this.userService.getModulePermissions(Modules.Onboarding),
      this.userService.getModulePermissionsById(companyId, Modules.Corporate),
      this.userService.getModulePermissionsById(companyId, Modules.Invoicing),
      this.userService.getModulePermissionsById(companyId, Modules.Risk),
      // Role-based effective permissions for the new company. Threaded through getCurrentUser
      // so the userId is fresh on every switch. catchError swallows failures into an empty map
      // — the legacy module flags still flow through, and *hasPermission falls back to "deny"
      // for the role-based surfaces until the next refresh.
      this.userService.getCurrentUser(false).pipe(
        switchMap(user => this.corporatePermissionsService.refreshEffectivePermissions(user.userId, companyId)),
        catchError(() => of(null))
      ),
    ]).subscribe(([onboardingPermissions, corporatePermissions, invoicingPermissions, riskPermissions]) => {
      localStorage.setItem('permissions', JSON.stringify(onboardingPermissions));
      localStorage.setItem('corporatePermissions', JSON.stringify(corporatePermissions));
      localStorage.setItem('invoicingPermissions', JSON.stringify(invoicingPermissions));
      localStorage.setItem('riskPermissions', JSON.stringify(riskPermissions));

      const urlTree = this.router.createUrlTree([route], queryParams ? { queryParams } : {});
      window.location.href = this.router.serializeUrl(urlTree);
    });
  }

  /**
   * Admin-only entry point: open another company's organization settings in a
   * new tab WITHOUT changing this tab's managed company. The settings wrapper
   * uses setCompanyIdObs (in-memory) instead of setManagedCompanyId (which
   * would write the shared localStorage). As a defensive belt-and-suspenders,
   * we also listen for storage events for ~5s and revert any managedCompanyId
   * write that arrives from the other tab.
   *
   * The settings page expects ?submittedFormId=<id> alongside the company id —
   * that's the company's internal-company SubmittedForm. We look it up first.
   */
  openCompanySettings(companyId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!companyId) return;

    this.formsService.getInternalCompanyFormId(companyId).pipe(
      catchError(() => of(null))
    ).subscribe(formId => {
      if (!formId) {
        this.snackBar.open('No internal client form found for this company.', 'Close', { duration: 3000 });
        return;
      }

      // Snapshot the persisted company so we can revert localStorage if any
      // tab writes a different value while the new tab is starting up. The
      // dropdown reads localStorage directly (currentCompanyIdFromStorage), so
      // protecting that one value is enough — the in-memory BehaviorSubject is
      // free to track whatever the new tab is doing without affecting the
      // originating tab's UI.
      const previousLs = localStorage.getItem('managedCompanyId');

      const urlTree = this.router.createUrlTree(['/settings/company', companyId], {
        queryParams: { submittedFormId: formId }
      });
      window.open(this.router.serializeUrl(urlTree), '_blank');

      if (previousLs !== null) {
        // Clear any pending lock from a previous click so they don't pile up.
        if (this.settingsLockInterval) clearInterval(this.settingsLockInterval);

        let elapsed = 0;
        this.settingsLockInterval = setInterval(() => {
          if (localStorage.getItem('managedCompanyId') !== previousLs) {
            localStorage.setItem('managedCompanyId', previousLs);
          }
          elapsed += 100;
          if (elapsed >= 5000) {
            clearInterval(this.settingsLockInterval);
            this.settingsLockInterval = null;
          }
        }, 100);
      }
    });
  }

  /**
   * Open another organization company's "as a client" form in a new tab without
   * changing the currently-managed company on this tab. The /edit-form/:id route's
   * managedCompanySwitchGuard looks up the form's owning company server-side and
   * switches the new tab's managed company in-memory — no URL param needed.
   */
  openCompanyAsForm(companyId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (!companyId || companyId === this.managedCompanyId) return;

    this.formsService.getInternalCompanyFormId(companyId).pipe(
      catchError(() => of(null))
    ).subscribe(formId => {
      if (!formId) {
        this.snackBar.open('No client form found for this company.', 'Close', { duration: 3000 });
        return;
      }
      const urlTree = this.router.createUrlTree(['/edit-form', formId]);
      window.open(this.router.serializeUrl(urlTree), '_blank');
    });
  }

  /**
   * Authoritative "what's the logged-in company" reading. The dropdown trigger
   * and the row highlight bind to this rather than `this.managedCompanyId` so
   * they always show the persisted company, even when a sibling action has
   * temporarily flipped the in-memory BehaviorSubject (e.g. opening org
   * settings of another company in a new tab keeps localStorage on the
   * current company, and the dropdown should reflect that, not the override).
   */
  get currentCompanyIdFromStorage(): number {
    const stored = localStorage.getItem('managedCompanyId');
    return stored ? +stored : 0;
  }

  /** Display label for the company-picker trigger. */
  get currentCompanyName(): string {
    if (!this.userCompanies?.length || !this.currentCompanyIdFromStorage) return '';
    return this.userCompanies.find(c => c.id === this.currentCompanyIdFromStorage)?.name || '';
  }

  /** Reset the filter when the picker menu opens. */
  onCompanyMenuOpened(): void {
    this.filteredCompanies = this.userCompanies || [];
  }

  onSearchFocus(): void {
    this.showSearchFilters = true;
    if (this.searchResults.length > 0) {
      this.showSearchResults = true;
    }
  }

  onSearchBlur(): void {
    // Delay to allow click events on results and filters
    setTimeout(() => {
      this.showSearchResults = false;
      if (this.activeSearchFilters.length === 0 && this.selectedSearchCompanyIds.length === 0) {
        this.showSearchFilters = false;
      }
    }, 200);
  }

  toggleSearchFilter(filterValue: string): void {
    const index = this.activeSearchFilters.indexOf(filterValue);
    if (index > -1) {
      this.activeSearchFilters.splice(index, 1);
    } else {
      this.activeSearchFilters.push(filterValue);
    }

    this.runSearch();
  }

  isFilterActive(filterValue: string): boolean {
    return this.activeSearchFilters.includes(filterValue);
  }

  toggleCompanyFilter(companyId: number): void {
    const idx = this.selectedSearchCompanyIds.indexOf(companyId);
    if (idx > -1) {
      this.selectedSearchCompanyIds.splice(idx, 1);
    } else {
      this.selectedSearchCompanyIds.push(companyId);
    }
    this.runSearch();
  }

  isCompanyFilterActive(companyId: number): boolean {
    return this.selectedSearchCompanyIds.includes(companyId);
  }

  selectAllSearchCompanies(): void {
    this.selectedSearchCompanyIds = [];
    this.runSearch();
  }

  get searchCompanyButtonLabel(): string {
    if (this.selectedSearchCompanyIds.length === 0) {
      return 'All companies';
    }
    if (this.selectedSearchCompanyIds.length === 1) {
      const company = this.userCompanies?.find(c => c.id === this.selectedSearchCompanyIds[0]);
      return company?.name || '1 company';
    }
    return `${this.selectedSearchCompanyIds.length} companies`;
  }

  private runSearch(): void {
    if (!this.searchControl.value || this.searchControl.value.length < 2) {
      return;
    }
    this.isSearching = true;
    this.globalSearchService.search(
      this.searchControl.value,
      this.activeSearchFilters,
      this.selectedSearchCompanyIds
    ).pipe(
      catchError(() => of([]))
    ).subscribe(results => {
      this.searchResults = this.filterResultsByCategory(results);
      this.isSearching = false;
      this.showSearchResults = this.searchResults.length > 0;
    });
  }

  getSearchPlaceholder(): string {
    if (this.activeSearchFilters.length === 0) {
      return 'Search clients, invoices, documents...';
    }
    const labels = this.activeSearchFilters.map(f =>
      this.searchFilterOptions.find(opt => opt.value === f)?.label || f
    );
    return `Search in ${labels.join(', ')}...`;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.searchResults = [];
    this.showSearchResults = false;
    this.showSearchFilters = false;
    this.activeSearchFilters = [];
    this.selectedSearchCompanyIds = [];
  }

  navigateToNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  navigateToNotificationSettings(): void {
    this.router.navigate(['/settings/notifications']);
  }

  toggleModuleMenu() {
    this.isModuleMenuOpen = !this.isModuleMenuOpen;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  navigateToSettings() {
    this.isSettingsOpen = !this.isSettingsOpen;
  }

  openModuleInNewTab(id: number) {
    this.isModuleMenuOpen = false;
    const urlTree = this.router.createUrlTree([''], { queryParams: { module: id } });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  selectModule(id: number) {
    this.isModuleMenuOpen = false;
    this.changeModule.emit(id);
    this.changeMenuModule(id);
    this.router.navigate([''], { queryParams: { module: id } }).then(() => {
      window.location.reload();
    });
  }

  hideAfterClick() {
    this.isSettingsOpen = false;
  }

  private changeMenuModule(id: number) {
    if (id === 1) {
      const menu = [
        {
          icon: 'table',
          name: 'Dashboard',
          route: 'corporate-forms-table'
        },
        {
          name: 'Time sheet',
          icon: 'schedule',
          route: 'time-sheet',
          children: [
            {
              name: 'Time sheet',
              route: 'time-sheet',
              icon: 'schedule',
              queryParam: null,
              children: null
            },
            {
              name: 'Profitability Report',
              route: 'profitability-report',
              icon: 'trending_up',
              queryParam: null,
              children: null
            },
          ]
        },
        {
          icon: 'people',
          name: 'Clients',
          route: 'forms-table',
          children: [
            {
              name: 'View All',
              route: 'forms-table',
              icon: 'visibility',
              queryParam: null,
              children: null
            },
            {
              name: 'Introducer',
              route: `create-form/${FormTypeEnum.Introducer}`,
              icon: 'supervisor_account',
              queryParam: null,
              children: [
                {
                  name: 'Create',
                  route: `create-form/${FormTypeEnum.Introducer}`,
                  icon: 'add',
                  queryParam: null,
                  children: null
                },
                {
                  name: 'View',
                  route: 'forms-table',
                  icon: 'visibility',
                  queryParam: FormTypeEnum.Introducer,
                  queryParams: { clientStatus: 'all', businessStatus: 'all' },
                  children: null
                }
              ]
            },
            {
              name: 'Business',
              route: `create-form/${FormTypeEnum.Business}`,
              icon: 'business',
              queryParam: null,
              children: [
                {
                  name: 'Create',
                  route: `create-form/${FormTypeEnum.Business}`,
                  icon: 'add',
                  queryParam: null,
                  children: null
                },
                {
                  name: 'View',
                  route: 'forms-table',
                  icon: 'visibility',
                  queryParam: FormTypeEnum.Business,
                  children: null
                }
              ]
            },
            {
              name: 'Individual',
              route: `create-form/${FormTypeEnum.Individual}`,
              icon: 'person',
              queryParam: null,
              children: [
                {
                  name: 'Create',
                  route: `create-form/${FormTypeEnum.Individual}`,
                  icon: 'add',
                  queryParam: null,
                  children: null
                },
                {
                  name: 'View',
                  route: 'forms-table',
                  icon: 'visibility',
                  queryParam: FormTypeEnum.Individual,
                  children: null
                }
              ]
            }
          ]
        },
        {
          name: 'Reports',
          icon: 'description',
          route: 'reports',
          children: null
        },
        {
          name: 'DMS',
          icon: 'file_present',
          route: 'file-system',
          children: null
        },
        {
          name: 'Planner',
          icon: 'checklist',
          route: 'task-planner',
          children: null
        },
        {
          name: 'Reference Numbers',
          icon: 'bookmark',
          route: 'reference-numbers',
          children: null
        }
      ];
      this.changeMenu.emit(menu);
    } else if (id === 2) {
      const menu = [
        {
          icon: 'dashboard',
          name: 'Dashboard',
          route: 'invoicing-dashboard'
        },
        {
          name: 'Documents',
          icon: 'description',
          route: 'invoicing/invoices-new',
          children: [
            {
              name: 'Invoices',
              route: 'invoicing/invoices-new',
              icon: 'receipt',
              queryParam: null,
              children: null
            },
            {
              name: 'Credit Notes',
              route: 'invoicing/credit-notes',
              icon: 'note',
              queryParam: null,
              children: null
            },
            {
              name: 'Quotations',
              route: 'invoicing/quotations',
              icon: 'request_quote',
              queryParam: null,
              children: null
            },
            {
              name: 'Receipts',
              route: 'invoicing/receipts',
              icon: 'receipt_long',
              queryParam: null,
              children: null
            },
            {
              name: 'Order/Proposal Form',
              route: 'invoicing/sales',
              icon: 'assignment',
              queryParam: null,
              children: null
            }
          ]
        },
        {
          name: 'Vendors & Expenses',
          icon: 'store',
          route: 'invoicing/vendors',
          children: [
            {
              name: 'Vendors',
              route: 'invoicing/vendors',
              icon: 'store',
              queryParam: null,
              children: null
            },
            {
              name: 'Expenses',
              route: 'invoicing/expenses',
              icon: 'payments',
              queryParam: null,
              children: null
            },
            {
              name: 'Disbursements',
              route: 'invoicing/disbursements-management',
              icon: 'account_balance',
              queryParam: null,
              children: null
            },
            {
              name: 'Actual Expenses',
              route: 'invoicing/actual-expenses-management',
              icon: 'price_check',
              queryParam: null,
              children: null
            }
          ]
        },
        {
          name: 'Settings',
          icon: 'settings',
          route: 'invoicing/new-payments',
          children: [
            {
              name: 'Payment Methods',
              route: 'invoicing/new-payments',
              icon: 'credit_card',
              queryParam: null,
              children: null
            },
            {
              name: 'Bank Accounts',
              route: 'invoicing/bank-accounts',
              icon: 'account_balance',
              queryParam: null,
              children: null
            },
            {
              name: 'VAT Rates',
              route: 'invoicing/vat-rates',
              icon: 'percent',
              queryParam: null,
              children: null
            },
            {
              name: 'Number Sequences',
              route: 'invoicing/number-sequences',
              icon: 'format_list_numbered',
              queryParam: null,
              children: null
            },
            {
              name: 'Chart Of Accounts',
              route: 'invoicing/chart-of-accounts',
              icon: 'account_tree',
              queryParam: null,
              children: null
            }
          ]
        }
      ];
      this.changeMenu.emit(menu);
    }
  }

  /**
   * Explicit "switch to this company" action from the picker menu. Prompts to
   * confirm, then writes the new managed company + permissions to localStorage and
   * reloads — same effect the old mat-select had on option-click.
   */
  switchToCompany(companyId: number, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    const previousCompanyId = this.companiesService.managedCompanyId;
    if (!companyId || companyId === previousCompanyId) return;

    this.confirmCompanySwitch(previousCompanyId, companyId).subscribe(confirmed => {
      if (!confirmed) return;
      this.applyCompanySwitch(companyId);
    });
  }

  private applyCompanySwitch(companyId: number): void {
    // Stop any pending settings-button lock so this intentional switch isn't
    // immediately reverted by the localStorage-restore polling loop.
    if (this.settingsLockInterval) {
      clearInterval(this.settingsLockInterval);
      this.settingsLockInterval = null;
    }
    this.companiesService.setManagedCompanyId(companyId);
    forkJoin([
      this.userService.getModulePermissions(Modules.Onboarding),
      this.userService.getModulePermissionsById(this.companiesService.managedCompanyId, Modules.Corporate),
      this.userService.getModulePermissionsById(this.companiesService.managedCompanyId, Modules.Invoicing),
      this.userService.getModulePermissionsById(this.companiesService.managedCompanyId, Modules.Risk)
    ]).subscribe(([onboardingPermissions, corporatePermissions, invoicingPermissions, riskPermissions]) => {
      localStorage.setItem('permissions', JSON.stringify(onboardingPermissions));
      localStorage.setItem('corporatePermissions', JSON.stringify(corporatePermissions));
      localStorage.setItem('invoicingPermissions', JSON.stringify(invoicingPermissions));
      localStorage.setItem('riskPermissions', JSON.stringify(riskPermissions));
      window.location.reload();
    });
  }

  private confirmCompanySwitch(fromCompanyId: number, toCompanyId: number): Observable<boolean> {
    const fromName = this.userCompanies?.find(c => c.id === fromCompanyId)?.name || 'current company';
    const toName = this.userCompanies?.find(c => c.id === toCompanyId)?.name || 'selected company';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Switch company?',
        text: `You are about to switch from "${fromName}" to "${toName}". Any unsaved changes on the current page will be lost.`,
        subText: 'Switch'
      }
    });

    return dialogRef.afterClosed().pipe(map(result => !!result));
  }

  onCompanySearch(value: string) {
    if (value !== '') {
      this.filteredCompanies = this.userCompanies
        .filter(company => company.name.toLowerCase().includes(value.toLowerCase()))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    } else {
      this.filteredCompanies = this.userCompanies;
    }
  }

  onLogout() {
    this.logout.emit();
  }

  getNavItemHref(item: any): string {
    if (!item.children || item.children.length === 0) {
      return this.buildNavHref(item);
    }
    return null;
  }

  buildNavHref(item: any): string {
    let route = '/' + item.route;
    const params: string[] = [];
    if (item.queryParam !== null && item.queryParam !== undefined) {
      params.push(`type=${item.queryParam}`);
    }
    if (item.queryParams) {
      Object.entries(item.queryParams).forEach(([key, value]) => {
        params.push(`${key}=${value}`);
      });
    }
    return params.length > 0 ? `${route}?${params.join('&')}` : route;
  }

  onNavItemClick(item: any, event?: MouseEvent): void {
    if (!item.children || item.children.length === 0) {
      // Allow ctrl+click / middle-click to open in new tab natively
      if (event && (event.ctrlKey || event.metaKey || event.button === 1)) {
        return;
      }
      event?.preventDefault();
      this.activeNavItem = item.name;
      this.modulesService.setModuleName(item.name);
      this.router.navigate(['/' + item.route]);
      this.navDropdownOpen = false;
    } else {
      // Parent items with children - just open the dropdown, don't navigate
      event?.preventDefault();
      if (this.navDropdownOpen && this.navDropdownItem?.name === item.name) {
        this.navDropdownOpen = false;
        this.navDropdownItem = null;
      } else {
        this.navDropdownOpen = true;
        this.navDropdownItem = item;
      }
    }
  }

  onNavChildClick(child: any, event?: MouseEvent): void {
    // Allow ctrl+click / middle-click to open in new tab natively
    if (event && (event.ctrlKey || event.metaKey || event.button === 1)) {
      return;
    }
    event?.preventDefault();

    this.navDropdownOpen = false;
    this.navDropdownItem = null;

    // If on the dashboard, open the corresponding tab instead of navigating away
    if (this.isOnDashboard()) {
      const route = child.route;
      const mapping = this.dashboardRouteMap[route];
      if (mapping) {
        this.router.navigate(['/invoicing-dashboard'], {
          queryParams: { view: mapping.view, tab: mapping.tab }
        });
        return;
      }
    }

    if (child.children && child.children.length > 0) {
      // Navigate to first sub-child
      const firstChild = child.children[0];
      this.router.navigate(['/' + firstChild.route], firstChild.queryParams ? { queryParams: firstChild.queryParams } : {});
    } else {
      if (child.queryParam !== null && child.queryParam !== undefined) {
        this.router.navigate(['/' + child.route], { queryParams: { type: child.queryParam } });
      } else if (child.queryParams) {
        this.router.navigate(['/' + child.route], { queryParams: child.queryParams });
      } else {
        this.router.navigate(['/' + child.route]);
      }
    }
  }

  private isOnDashboard(): boolean {
    return this.router.url.startsWith('/invoicing-dashboard');
  }

  isNavItemActive(item: any): boolean {
    const currentUrl = this.router.url;
    if (currentUrl.includes(item.route)) return true;
    if (item.children) {
      return item.children.some((child: any) => {
        if (currentUrl.includes(child.route)) return true;
        if (child.children) {
          return child.children.some((sub: any) => currentUrl.includes(sub.route));
        }
        return false;
      });
    }
    return false;
  }

  onClose(event) {
    this.filteredCompanies = !event ? this.userCompanies : this.filteredCompanies;
    this.companyFilter.nativeElement.value = '';
  }

  toggleMeetingNotesPanel(): void {
    this.meetingNotesPanelService.togglePanel();
  }

  toggleAiChat(): void {
    this.aiChatPanelService.toggle();
  }

  /** Href used by the Contacts anchor so Ctrl/⌘/middle-click open the real new tab. */
  get phonebookUrl(): string {
    return this.router.serializeUrl(this.router.createUrlTree(['/phonebook']));
  }

  /**
   * Contacts header action. A plain click toggles the slide-in phonebook
   * panel (how it worked before the standalone page existed). Modifier or
   * middle clicks fall through to the anchor's href so the browser opens the
   * full /phonebook page in a new tab/window.
   */
  onContactsClick(event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button === 1) {
      event.stopPropagation();
      return; // let the browser handle the new tab/window
    }
    event.preventDefault();
    event.stopPropagation();
    this.phonebookSidebarService.toggle();
  }

  /** Open the contacts/phonebook as a standalone full page in a new browser tab. */
  openPhonebookTab(): void {
    window.open(this.phonebookUrl, '_blank');
  }

  /** Open the "new contact" dialog from anywhere in the app. */
  addContact(): void {
    this.phonebookSidebarService.requestCreate();
  }

  private redirectToMeetingNote(note: NotificationVM) {
    const urlTree = this.router.createUrlTree(['/tagged-notes'], {
      queryParams: {
        noteId: note.rootMeetingNoteId || note.meetingNoteId,
        formId: note.submittedFormId,
      }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  private startNotificationPolling(): void {
    if (this.isPolling) return;
    this.isPolling = true;

    interval(this.POLLING_INTERVAL).pipe(
      takeUntilDestroyed(this.destroy)
    ).subscribe(() => this.refreshNotifications());
  }

  private refreshNotifications(): void {
    this.formsService.getAllNotifications().pipe(
      catchError((err) => {
        console.error('Error refreshing notifications:', err);
        return of([]);
      })
    ).subscribe((rawNotifications) => {
      // Only update if there are actual changes to avoid unnecessary re-renders
      if (this.hasNotificationsChanged(rawNotifications)) {
        const enriched = rawNotifications.map(n => enrichNotification(n));
        // Update existing notifications in place to preserve UI state
        const existingIds = new Set(this.notifications.map(n => n.id));
        const newIds = new Set(enriched.map(n => n.id));

        // Check if we have structural changes (different IDs)
        const hasStructuralChange = existingIds.size !== newIds.size ||
          [...existingIds].some(id => !newIds.has(id));

        if (hasStructuralChange) {
          this.notifications = enriched;
        } else {
          // Update in place
          enriched.forEach(newNotif => {
            const existing = this.notifications.find(n => n.id === newNotif.id);
            if (existing) {
              Object.assign(existing, newNotif);
            }
          });
        }
      }
    });
  }

  private hasNotificationsChanged(newNotifications: any[]): boolean {
    if (this.notifications.length !== newNotifications.length) return true;

    for (const newNotif of newNotifications) {
      const existing = this.notifications.find(n => n.id === newNotif.id);
      if (!existing) return true;
      if (existing.isRead !== newNotif.isRead ||
          existing.message !== newNotif.message) {
        return true;
      }
    }
    return false;
  }
}
